import { useRef, useState } from "react";

import { extractErrorMessage } from "@/services/axiosConfig";
import type { Certificate } from "@/types/certificate";

import { Button } from "./Button";
import { Card, CardBody, CardHead } from "./Card";
import { Checkbox } from "./Checkbox";
import { Icon } from "./Icon";
import { Modal } from "./Modal";
import { Pill } from "./Pill";
import "./CertificateCard.css";

function fmtDate(iso: string | null): string {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  const p = (n: number) => String(n).padStart(2, "0");
  return `${p(d.getDate())}/${p(d.getMonth() + 1)}/${d.getFullYear()}`;
}

/** Card de certificado A1 reutilizável (org/empresa). Mostra o cert ativo e, com
 *  `canManage`, permite anexar/substituir (modal) e revogar. */
export function CertificateCard({
  cert,
  isLoading = false,
  canManage,
  onUpload,
  onRevoke,
}: {
  cert: Certificate | null | undefined;
  isLoading?: boolean;
  canManage: boolean;
  onUpload: (file: File, password: string) => Promise<unknown>;
  onRevoke: () => Promise<unknown>;
}) {
  const [open, setOpen] = useState(false);
  const [file, setFile] = useState<File | null>(null);
  const [password, setPassword] = useState("");
  const [accepted, setAccepted] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const fileRef = useRef<HTMLInputElement | null>(null);

  const reset = () => {
    setFile(null);
    setPassword("");
    setAccepted(false);
    setError(null);
  };
  const close = () => {
    setOpen(false);
    reset();
  };

  const submit = async () => {
    if (!file || !password || !accepted) {
      setError("Selecione o arquivo, informe a senha e autorize o armazenamento.");
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await onUpload(file, password);
      close();
    } catch (e) {
      setError(extractErrorMessage(e));
    } finally {
      setBusy(false);
    }
  };

  const revoke = async () => {
    if (!window.confirm("Revogar o certificado ativo? As automações dependem dele.")) return;
    try {
      await onRevoke();
    } catch (e) {
      window.alert(extractErrorMessage(e));
    }
  };

  return (
    <Card>
      <CardHead
        title="Certificado digital (A1)"
        actions={cert?.active ? <Pill tone="success">Ativo</Pill> : undefined}
      />
      <CardBody>
        {isLoading ? (
          <p className="cert-muted">Carregando…</p>
        ) : cert ? (
          <div className="cert-row">
            <span className="cert-mark">
              <Icon name="shield-check" size={18} />
            </span>
            <div className="cert-info">
              <div className="cert-filename">{cert.filename}</div>
              <div className="cert-validade">Validade: {fmtDate(cert.valid_until)}</div>
            </div>
            {canManage && (
              <div className="cert-actions">
                <Button variant="ghost" size="sm" iconLeft="plus" onClick={() => setOpen(true)}>
                  Substituir
                </Button>
                <Button variant="danger" size="sm" iconLeft="x" onClick={() => void revoke()}>
                  Revogar
                </Button>
              </div>
            )}
          </div>
        ) : (
          <div className="cert-empty">
            <p className="cert-muted">
              Nenhum certificado anexado. Sem ele, o e-CAC não pode ser acessado em nome desta
              entidade (procurações, importação e transmissão dependem disso).
            </p>
            {canManage && (
              <Button variant="primary" size="sm" iconLeft="plus" onClick={() => setOpen(true)}>
                Anexar certificado
              </Button>
            )}
          </div>
        )}
      </CardBody>

      {open && (
        <Modal
          onClose={close}
          title="Anexar certificado A1"
          subtitle="Arquivo .pfx/.p12 + senha. Guardamos cifrado, uso restrito ao e-CAC."
          size="sm"
          footer={
            <>
              <Button variant="ghost" onClick={close} disabled={busy}>
                Cancelar
              </Button>
              <Button variant="primary" onClick={() => void submit()} disabled={busy}>
                {busy ? "Enviando…" : "Enviar"}
              </Button>
            </>
          }
        >
          <div className="cert-drop">
            <Icon name="file-text" size={20} />
            <div className="cert-drop-text">
              {file ? file.name : "Selecione o arquivo .pfx ou .p12 (até 4 MB)"}
            </div>
            <input
              ref={fileRef}
              type="file"
              accept=".pfx,.p12"
              style={{ display: "none" }}
              onChange={(e) => setFile(e.target.files?.[0] ?? null)}
            />
            <Button variant="ghost" size="sm" onClick={() => fileRef.current?.click()}>
              {file ? "Trocar" : "Selecionar"}
            </Button>
          </div>

          <label className="cert-field">
            <span>Senha do certificado</span>
            <input
              type="password"
              maxLength={120}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="••••••••"
            />
          </label>

          <div className="cert-accept">
            <Checkbox checked={accepted} onChange={setAccepted} />
            <span>
              Autorizo armazenar o certificado e a senha cifrados, usados exclusivamente para
              acessar o e-CAC em nome desta entidade.
            </span>
          </div>

          {error && <p className="cert-error">{error}</p>}
        </Modal>
      )}
    </Card>
  );
}
